import { useState, useEffect, ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { AdminAuthGate } from './AdminAuthGate';
import { checkAdminAuthSession } from '@/utils/deviceFingerprint';
import { useNavigate } from 'react-router-dom';
import { useToast } from '@/hooks/use-toast';
import { supabase } from '@/integrations/supabase/client';

interface GlobalSecurityGateProps {
  children: ReactNode;
}

export function GlobalSecurityGate({ children }: GlobalSecurityGateProps) {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [isCheckingSession, setIsCheckingSession] = useState(true);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    const checkSession = async () => {
      // 1. Check if security gate is enabled in global_settings
      const { data: settings, error } = await supabase
        .from('global_settings')
        .select('enable_security_gate')
        .single();
      
      if (error) {
        // Fallback: Bei Fehler Gate deaktivieren (fail-open)
        console.error('[GlobalSecurityGate] Failed to load settings', error);
        setIsAuthenticated(true);
        setIsCheckingSession(false);
        return;
      }
      
      // If gate is disabled, everyone gets in
      if (!settings?.enable_security_gate) {
        setIsAuthenticated(true);
        setIsCheckingSession(false);
        return;
      }
      
      // If gate is enabled, EVERYONE must authenticate with PW + TOTP
      const hasValidSession = await checkAdminAuthSession();
      setIsAuthenticated(hasValidSession);
      setIsCheckingSession(false);
    };
    checkSession();
  }, []);

  if (isCheckingSession) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Checking authentication...</p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4 bg-background">
        <AdminAuthGate onAuthComplete={() => setIsAuthenticated(true)} />
      </div>
    );
  }

  return <>{children}</>;
}
